import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import Link from '@atlaskit/link';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { useMessageEvent } from '@post-office/message-lifecycle';

import ChromeIcon from '../../../../../assets/chrome-icon.svg';
import RovoIcon from '../../../../../assets/rovo-icon.svg';
import { useIntl } from 'react-intl';

import { COPY_VARIATIONS, registeredContent } from '../messages';
import { type JSX } from 'react/jsx-runtime';

const styles = cssMap({
	boxContainer: {
		display: 'flex',
		width: '100%',
		padding: token('space.150', '12px'),
		alignItems: 'flex-start',
		gap: token('space.100', '8px'),
		borderRadius: token('radius.small', '4px'),
		backgroundColor: token('color.background.accent.blue.subtlest'),
		placeContent: 'center',
	},
	contentStack: {
		gap: token('space.100', '8px'),
		width: '100%',
	},
	headerRow: {
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		width: '100%',
	},
	iconAndTitle: {
		gap: token('space.100', '8px'),
	},
	iconContainer: {
		display: 'flex',
		alignItems: 'center',
	},
});

const getLinkUrl = (cohort: string) => {
	const UTM_CONTENT = `side_nav_install_cta_${cohort}`;
	return `https://chromewebstore.google.com/detail/rovo-search-chat-and-more/npbjhobkibekbklkghlhfhieggcggpdb?utm_source=confluence-side-nav&utm_medium=banner&utm_campaign=rovo_extension_side_nav_install_cta&utm_content=${UTM_CONTENT}`;
};

const selectCopyForCohort = (cohort: string) => {
	return (
		COPY_VARIATIONS[cohort as keyof typeof COPY_VARIATIONS] || COPY_VARIATIONS['copy-version-b']
	);
};

type LeftNavCtaMessageProps = {
	context: {
		experimentCohort: string;
		[key: string]: unknown;
	};
	handleOnDismissed: () => void;
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
};

export const LeftNavCtaMessage = ({
	context,
	handleOnDismissed,
	onSideNavClick,
}: LeftNavCtaMessageProps): JSX.Element => {
	const { experimentCohort } = context;

	const { formatMessage } = useIntl();
	const { heading, description } = selectCopyForCohort(experimentCohort);
	const { messageClicked } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();

	const headingText = formatMessage(heading);
	const descriptionText = formatMessage(description);
	const linkText = formatMessage(registeredContent.default.linkText);
	const rovoIconAlt = formatMessage(registeredContent.default.rovoIconAlt);
	const chromeIconAlt = formatMessage(registeredContent.default.chromeIconAlt);
	const dismissLabel = formatMessage(registeredContent.default.dismissLabel);

	const linkUrl = getLinkUrl(experimentCohort);

	const handleDismiss = () => {
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source: 'confluence-side-nav',
			},
		});
		handleOnDismissed();
	};

	const handleLinkClick = () => {
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'installLink',
				clickedElement: 'link',
			},
			action: {
				actionType: 'open',
				destinationUrl: linkUrl,
			},
			additionalMetadataFromFrontend: {
				experimentCohort,
				source: 'confluence-side-nav',
			},
		});
		void onSideNavClick('ROVO_EXTENSION_CTA');
	};

	return (
		<Box xcss={cx(styles.boxContainer)} style={{ border: `1px solid ${token('color.border')}` }}>
			<Stack xcss={cx(styles.contentStack)} testId="rovo-left-nav-content">
				<Inline xcss={cx(styles.headerRow)}>
					<Inline xcss={cx(styles.iconAndTitle)} alignBlock="center">
						<Box xcss={cx(styles.iconContainer)}>
							<Image
								src={RovoIcon}
								alt={rovoIconAlt}
								width={24}
								height={24}
								style={{ flexShrink: 0, zIndex: 2 }}
							/>
							<Image
								src={ChromeIcon}
								alt={chromeIconAlt}
								width={24}
								height={24}
								style={{ marginLeft: '-8px', flexShrink: 0, zIndex: 1 }}
							/>
						</Box>
						<Heading size="xsmall">{headingText}</Heading>
					</Inline>
					<IconButton
						icon={CrossIcon}
						label={dismissLabel}
						appearance="subtle"
						spacing="compact"
						onClick={handleDismiss}
					/>
				</Inline>
				<Text size="small">{descriptionText}</Text>
				<Link href={linkUrl} target="blank" onClick={handleLinkClick}>
					{linkText}
				</Link>
			</Stack>
		</Box>
	);
};
